import React, { Fragment, useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Button, Table } from 'react-bootstrap';

// --- Redux Actions ---
import { listDoctorPayouts } from 'src/actions/doctorPayoutAction';
import { checkPayoutStatus } from 'src/actions/payoutActions';
import { listDoctorInvoices } from 'src/actions/invoiceActions'; // Needed for patient info
import { getAllPatientProfiles } from 'src/actions/patientProfileActions'; // Needed for patient info
import { imgUrl } from 'src/actions/types';
import { formatCurrency } from "../utils/currencyHelper";
// --- Helper Functions ---
const getPayoutBadgeClass = (status) => {
    switch (status) {
        case 'completed': case 'processed': return 'complete-label';
        case 'processing': case 'queued': return 'processing-label';
        case 'failed': case 'reversed': return 'failed-label';
        default: return 'bg-secondary-light';
    }
};

// ===================================================================================
// START OF MODIFIED BLOCK
// 1. Added a new helper function to show user-friendly statuses.
// ===================================================================================
const getDisplayStatus = (status) => {
    switch (status) {
        case 'completed':
        case 'processed':
            return 'Success';
        case 'processing':
        case 'queued':
            return 'Processing';
        case 'failed':
        case 'reversed':
            return 'Failed';
        default:
            // This will handle any other status, like 'Awaiting', or return it as is.
            return status;
    }
};
// ===================================================================================
// END OF MODIFIED BLOCK
// ===================================================================================

const formatDate = (dateValue, options = { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric' }) => {
    if (!dateValue) return 'N/A';
    const date = new Date(dateValue);
    return date.toLocaleString(undefined, options);
};


// --- Main Component ---
const PayoutHistory = () => {
    const dispatch = useDispatch();

    // --- Redux State ---
    const { loading, error, payoutRecords } = useSelector((state) => state.doctorPayout);
    const { invoices } = useSelector((state) => state.invoice);
    const { profiles: patients } = useSelector((state) => state.patientProfile);
     const { selectedCurrency } = useSelector((state) => state.currencyReducer || { selectedCurrency: 'INR' });
    // --- Local State ---
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isSyncing, setIsSyncing] = useState(false);
    const [gatewayDetails, setGatewayDetails] = useState(new Map());
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
   

    useEffect(() => {
        const fetchAndSyncData = async () => {
            await dispatch(listDoctorInvoices());
            await dispatch(getAllPatientProfiles());
            await dispatch(listDoctorPayouts());
        };
        fetchAndSyncData();
    }, [dispatch]);
    const enrichedPayoutRecords = useMemo(() => {
        if (!payoutRecords) return [];

        const invoiceMap = invoices.length > 0 ? new Map(invoices.map(i => [String(i.id), i])) : new Map();
        const patientMap = patients.length > 0 ? new Map(patients.map(p => [p.PatientID, p])) : new Map();
        
        return payoutRecords.map(record => {
            const payoutId = record.payoutId || record.payoutid;
            const invoiceDetails = invoiceMap.get(String(record.invoiceId || record.InvoiceId)) || {};
            const patientDetails = patientMap.get(invoiceDetails.PatientID) || {};
            
            let date = record.createdAt;
            let status = record.paymentStatus;

            if (payoutId && gatewayDetails.has(payoutId)) {
                const details = gatewayDetails.get(payoutId);
                date = details.gateway_created_at || details.initiated_at || date;
                status = details.status || status;
            }

            return {
                ...record,
                invoiceDetails,
                patientDetails,
                displayDate: date,
                displayStatus: status,
            };
        }).sort((a, b) => new Date(b.displayDate) - new Date(a.displayDate));

    }, [payoutRecords, invoices, patients, gatewayDetails]);

const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = enrichedPayoutRecords.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(enrichedPayoutRecords.length / itemsPerPage);

    useEffect(() => {
        setCurrentPage(1); // Reset to page 1 if items per page changes
    }, [itemsPerPage]);
    
const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const renderItemsPerPageSelector = () => (
        <div className="dataTables_length">
            <label>
                Items: 
                <select 
                    className="form-control form-control-sm ml-2"
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    style={{ width: '70px', display: 'inline-block' }}
                >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                </select>
            </label>
        </div>
    );

    const renderPagination = () => {
        const pageNumbers = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

        return (
            <div className="dataTables_paginate paging_simple_numbers">
                <ul className="pagination">
                    <div className="dataTables_info mr-3 mt-2">
                        Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, enrichedPayoutRecords.length)} of {enrichedPayoutRecords.length} entries
                    </div>
                    <li className={`paginate_button page-item previous ${currentPage === 1 ? 'disabled' : ''}`}>
                        <a href="#" onClick={(e) => { e.preventDefault(); if (currentPage > 1) handlePageChange(currentPage - 1); }} className="page-link">Previous</a>
                    </li>
                    {pageNumbers.map(number => (
                        <li key={number} className={`paginate_button page-item ${currentPage === number ? 'active' : ''}`}>
                            <a href="#" onClick={(e) => { e.preventDefault(); handlePageChange(number); }} className="page-link">{number}</a>
                        </li>
                    ))}
                    <li className={`paginate_button page-item next ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <a href="#" onClick={(e) => { e.preventDefault(); if (currentPage < totalPages) handlePageChange(currentPage + 1); }} className="page-link">Next</a>
                    </li>
                </ul>
            </div>
        );
    };

    // --- Modal Handlers ---
    const handleViewDetailsClick = (record) => {
        setSelectedRecord(record);
        setShowDetailsModal(true);
    };

    const handleCloseModal = () => {
        setShowDetailsModal(false);
        setSelectedRecord(null);
    };

    return (
        <Fragment>
            <style>{`
    .pagination-wrapper {
        padding: 15px 0;
        border-top: 1px solid #dee2e6;
    }
    .pagination .page-item.active .page-link {
        background-color: #0084d9;
        border-color: #0084d9;
        color: white;
    }
    .pagination .page-link {
        color: #0084d9;
    }
    .dataTables_info {
        font-size: 0.85rem;
        color: #6c757d;
    }
`}</style>
            {/* Page Header */}
            <div className="page-header filter-bar rounded">
                <div className="row align-items-center">
                    <div className="col">
                        <h4 className="page-title">My Payout History</h4>
                        <ul className="breadcrumb">
                            <li className="breadcrumb-item"><Link to="/doctor/dashboard">Doctor Dashboard</Link></li>
                            <li className="breadcrumb-item active ">Payouts</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Main Content: Table */}
            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="table-responsive">
                                <table className="datatable table table-hover table-center mb-0">
                                    {/* =================================================================================== */}
                                    {/* START OF MODIFIED BLOCK */}
                                    {/* 2. Added a 'Date' column to the table header. */}
                                    {/* =================================================================================== */}
                                    <thead className=''>
                                        <tr>
                                            <th>Payout ID</th>
                                            <th>Date</th>
                                            <th>Patient</th>
                                            <th>Invoice ID</th>
                                            <th>Amount</th>
                                            <th>Status</th>
                                            <th className="text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    {/* =================================================================================== */}
                                    {/* END OF MODIFIED BLOCK */}
                                    {/* =================================================================================== */}
                                    <tbody>
                                        {loading && !isSyncing ? (
                                            <tr><td colSpan="7" className="text-center">Loading Payouts...</td></tr>
                                        ) : error ? (
                                            <tr><td colSpan="7" className="text-center text-danger">Error: {error}</td></tr>
                                        ) : currentItems.length > 0 ? (
    currentItems.map((record) => (
                                                <tr key={record.id}>
                                                    <td>{record.payoutId || record.payoutid || 'N/A'}</td>
                                                    {/* =================================================================================== */}
                                                    {/* START OF MODIFIED BLOCK */}
                                                    {/* 3. Added a cell to display the formatted date. */}
                                                    {/* =================================================================================== */}
                                                    <td>{formatDate(record.date)}</td>
                                                    {/* =================================================================================== */}
                                                    {/* END OF MODIFIED BLOCK */}
                                                    {/* =================================================================================== */}
                                                    <td>
                                                        <h2 className="table-avatar">
                                                            <Link to={`/patient-profile/${record.patientDetails.PatientID}`} className="avatar avatar-sm mr-2">
                                                                <img className="avatar-img rounded-circle" src={record.patientDetails.uploadPhoto ? `${imgUrl}${record.patientDetails.uploadPhoto}` : '/assets/img/patients/patient.jpg'} alt="Patient" />
                                                            </Link>
                                                            <Link to={`/patient-profile/${record.patientDetails.PatientID}`}>{record.patientDetails.firstName || 'Unknown'} {record.patientDetails.lastName || ''}</Link>
                                                        </h2>
                                                    </td>
                                                    <td>#{record.invoiceId || record.InvoiceId}</td>
                                                    <td>{formatCurrency(record.Amount || 0, selectedCurrency)}</td>
                                                    <td>
                                                        {/* =================================================================================== */}
                                                        {/* START OF MODIFIED BLOCK */}
                                                        {/* 4. Used getDisplayStatus() to show the simplified status. */}
                                                        {/* =================================================================================== */}
                                                        <span className={`badge badge-pill ${getPayoutBadgeClass(record.displayStatus)}`}>
                                                            {getDisplayStatus(record.displayStatus)}
                                                        </span>
                                                        {/* =================================================================================== */}
                                                        {/* END OF MODIFIED BLOCK */}
                                                        {/* =================================================================================== */}
                                                    </td>
                                                    <td className="text-right">
                                                        <button variant="light" title='View Details' size="sm" className="i-btn-view" onClick={() => handleViewDetailsClick(record)}>
                                                           
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr><td colSpan="7" className="text-center">No payout history found.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                             {enrichedPayoutRecords.length > 0 && (
                <div className="pagination-wrapper mt-3 d-flex justify-content-between align-items-center flex-wrap">
                    <div className="items-per-page-wrapper">
                        {renderItemsPerPageSelector()}
                    </div>
                    <div className="pagination-controls-wrapper">
                        {renderPagination()}
                    </div>
                </div>
            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Payout Details Modal */}
            {selectedRecord && (
                <Modal show={showDetailsModal} onHide={handleCloseModal} centered>
                    <Modal.Header className='bg-info text-white ' closeButton closeVariant="white">
                        <Modal.Title>Payout Record Details</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p><strong>Database Record ID:</strong> {selectedRecord.id}</p>
                        <p><strong>Payout ID (Gateway):</strong> <span className="text-primary">{selectedRecord.payoutId || selectedRecord.payoutid}</span></p>
                        <hr/>
                        <p><strong>Patient:</strong> {selectedRecord.patientDetails.firstName || 'Unknown'} {selectedRecord.patientDetails.lastName || ''}</p>
                        <hr/>
                        <p><strong>Invoice ID:</strong> #{selectedRecord.invoiceId || selectedRecord.InvoiceId}</p>
                        <p><strong>Amount:</strong> {formatCurrency(selectedRecord.Amount || 0, selectedCurrency)}</p>
                        {/* =================================================================================== */}
                        {/* START OF MODIFIED BLOCK */}
                        {/* 5. Used getDisplayStatus() in the modal for consistency. */}
                        {/* =================================================================================== */}
                        <p><strong>Status:</strong> {getDisplayStatus(selectedRecord.displayStatus)}</p>
                        {/* =================================================================================== */}
                        {/* END OF MODIFIED BLOCK */}
                        {/* =================================================================================== */}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="btn btn-secondary rounded-pill" onClick={handleCloseModal}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </Fragment>
    );
}

export default PayoutHistory;


























// import React, { Fragment, useEffect, useMemo, useState, useCallback } from 'react';
// import { Link } from 'react-router-dom';
// import { useDispatch, useSelector } from 'react-redux';
// import { Modal, Button } from 'react-bootstrap';

// // --- Redux Actions ---
// import { listDoctorPayouts } from 'src/actions/doctorPayoutAction';
// import { checkPayoutStatus } from 'src/actions/payoutActions';
// import { listDoctorInvoices } from 'src/actions/invoiceActions';
// import { getAllPatientProfiles } from 'src/actions/patientProfileActions';
// import { imgUrl } from 'src/actions/types';
// import { formatCurrency } from "../utils/currencyHelper";

// // --- Helper Functions ---
// const getPayoutBadgeClass = (status) => {
//     switch (status) {
//         case 'completed': 
//         case 'processed': 
//             return 'complete-label';
//         case 'processing': 
//         case 'queued': 
//             return 'processing-label';
//         case 'failed': 
//         case 'reversed': 
//             return 'failed-label';
//         default: 
//             return 'bg-secondary-light';
//     }
// };

// const getDisplayStatus = (status) => {
//     switch (status) {
//         case 'completed':
//         case 'processed':
//             return 'Success';
//         case 'processing':
//         case 'queued':
//             return 'Processing';
//         case 'failed':
//         case 'reversed':
//             return 'Failed';
//         default:
//             return status || 'Awaiting';
//     }
// };

// const formatDate = (dateValue, options = { 
//     year: 'numeric', 
//     month: 'short', 
//     day: 'numeric', 
//     hour: 'numeric', 
//     minute: 'numeric' 
// }) => {
//     if (!dateValue) return 'N/A';
//     const date = new Date(dateValue);
//     return date.toLocaleString(undefined, options);
// };

// // --- Main Component ---
// const PayoutHistory = () => {
//     const dispatch = useDispatch();

//     // --- Redux State ---
//     const { loading, error, payoutRecords } = useSelector((state) => state.doctorPayout);
//     const { invoices } = useSelector((state) => state.invoice);
//     const { profiles: patients } = useSelector((state) => state.patientProfile);
//     const { selectedCurrency } = useSelector((state) => state.currencyReducer || { selectedCurrency: 'INR' });

//     // --- Local State ---
//     const [showDetailsModal, setShowDetailsModal] = useState(false);
//     const [selectedRecord, setSelectedRecord] = useState(null);
//     const [isSyncing, setIsSyncing] = useState(false);
//     const [gatewayDetails, setGatewayDetails] = useState(new Map());
//     const [currentPage, setCurrentPage] = useState(1);
//     const [itemsPerPage, setItemsPerPage] = useState(10);

//     // --- Data Fetching ---
//     useEffect(() => {
//         const fetchAndSyncData = async () => {
//             try {
//                 await dispatch(listDoctorInvoices());
//                 await dispatch(getAllPatientProfiles());
//                 await dispatch(listDoctorPayouts());
//             } catch (error) {
//                 console.error("Error fetching data:", error);
//             }
//         };
//         fetchAndSyncData();
//     }, [dispatch]);

//     // --- Enrich Payout Records with Memoization ---
//     const enrichedPayoutRecords = useMemo(() => {
//         if (!payoutRecords || !Array.isArray(payoutRecords)) return [];

//         const invoiceMap = invoices && invoices.length > 0 
//             ? new Map(invoices.map(i => [String(i.id), i])) 
//             : new Map();
        
//         const patientMap = patients && patients.length > 0 
//             ? new Map(patients.map(p => [p.PatientID, p])) 
//             : new Map();
        
//         return payoutRecords.map(record => {
//             const payoutId = record.payoutId || record.payoutid;
//             const invoiceDetails = invoiceMap.get(String(record.invoiceId || record.InvoiceId)) || {};
//             const patientDetails = patientMap.get(invoiceDetails.PatientID) || {};
            
//             let date = record.createdAt || record.created_at || new Date().toISOString();
//             let status = record.paymentStatus || record.status;

//             if (payoutId && gatewayDetails.has(payoutId)) {
//                 const details = gatewayDetails.get(payoutId);
//                 date = details.gateway_created_at || details.initiated_at || date;
//                 status = details.status || status;
//             }

//             return {
//                 ...record,
//                 invoiceDetails,
//                 patientDetails,
//                 displayDate: date,
//                 displayStatus: status,
//                 date: date // For table display
//             };
//         }).sort((a, b) => new Date(b.displayDate) - new Date(a.displayDate));
//     }, [payoutRecords, invoices, patients, gatewayDetails]);

//     // --- Pagination Calculations ---
//     const indexOfLastItem = currentPage * itemsPerPage;
//     const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//     const currentItems = enrichedPayoutRecords.slice(indexOfFirstItem, indexOfLastItem);
//     const totalPages = Math.ceil(enrichedPayoutRecords.length / itemsPerPage);

//     // --- Reset page when items per page changes ---
//     useEffect(() => {
//         setCurrentPage(1);
//     }, [itemsPerPage]);

//     // --- Pagination Handlers ---
//     const handlePageChange = (pageNumber) => {
//         if (pageNumber >= 1 && pageNumber <= totalPages) {
//             setCurrentPage(pageNumber);
//         }
//     };

//     const handleItemsPerPageChange = (e) => {
//         setItemsPerPage(Number(e.target.value));
//     };

//     // --- Render Items Per Page Selector ---
//     const renderItemsPerPageSelector = () => (
//         <div className="dataTables_length">
//             <label className="d-flex align-items-center">
//                 <span className="mr-2">Items:</span>
//                 <select 
//                     className="form-control form-control-sm"
//                     value={itemsPerPage}
//                     onChange={handleItemsPerPageChange}
//                     style={{ width: '70px' }}
//                 >
//                     <option value="5">5</option>
//                     <option value="10">10</option>
//                     <option value="20">20</option>
//                     <option value="50">50</option>
//                 </select>
//             </label>
//         </div>
//     );

//     // --- Render Pagination ---
//     const renderPagination = () => {
//         if (totalPages <= 1) return null;

//         const pageNumbers = [];
//         const maxVisiblePages = 5;
//         let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
//         let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

//         if (endPage - startPage + 1 < maxVisiblePages) {
//             startPage = Math.max(1, endPage - maxVisiblePages + 1);
//         }

//         for (let i = startPage; i <= endPage; i++) {
//             pageNumbers.push(i);
//         }

//         return (
//             <div className="dataTables_paginate paging_simple_numbers">
//                 <ul className="pagination mb-0">
//                     <li className={`paginate_button page-item previous ${currentPage === 1 ? 'disabled' : ''}`}>
//                         <button 
//                             onClick={() => handlePageChange(currentPage - 1)} 
//                             className="page-link"
//                             disabled={currentPage === 1}
//                         >
//                             Previous
//                         </button>
//                     </li>
                    
//                     {startPage > 1 && (
//                         <>
//                             <li className="paginate_button page-item">
//                                 <button onClick={() => handlePageChange(1)} className="page-link">1</button>
//                             </li>
//                             {startPage > 2 && <li className="paginate_button page-item disabled"><span className="page-link">...</span></li>}
//                         </>
//                     )}
                    
//                     {pageNumbers.map(number => (
//                         <li key={number} className={`paginate_button page-item ${currentPage === number ? 'active' : ''}`}>
//                             <button 
//                                 onClick={() => handlePageChange(number)} 
//                                 className="page-link"
//                             >
//                                 {number}
//                             </button>
//                         </li>
//                     ))}
                    
//                     {endPage < totalPages && (
//                         <>
//                             {endPage < totalPages - 1 && <li className="paginate_button page-item disabled"><span className="page-link">...</span></li>}
//                             <li className="paginate_button page-item">
//                                 <button onClick={() => handlePageChange(totalPages)} className="page-link">{totalPages}</button>
//                             </li>
//                         </>
//                     )}
                    
//                     <li className={`paginate_button page-item next ${currentPage === totalPages ? 'disabled' : ''}`}>
//                         <button 
//                             onClick={() => handlePageChange(currentPage + 1)} 
//                             className="page-link"
//                             disabled={currentPage === totalPages}
//                         >
//                             Next
//                         </button>
//                     </li>
//                 </ul>
//             </div>
//         );
//     };

//     // --- Modal Handlers ---
//     const handleViewDetailsClick = (record) => {
//         setSelectedRecord(record);
//         setShowDetailsModal(true);
//     };

//     const handleCloseModal = () => {
//         setShowDetailsModal(false);
//         setSelectedRecord(null);
//     };

//     return (
//         <Fragment>
//             {/* Page Header */}
//             <div className="page-header filter-bar rounded">
//                 <div className="row align-items-center">
//                     <div className="col">
//                         <h4 className="page-title">My Payout History</h4>
//                         <ul className="breadcrumb">
//                             <li className="breadcrumb-item">
//                                 <Link to="/doctor/dashboard">Doctor Dashboard</Link>
//                             </li>
//                             <li className="breadcrumb-item active">Payouts</li>
//                         </ul>
//                     </div>
//                 </div>
//             </div>

//             {/* Main Content: Table */}
//             <div className="row">
//                 <div className="col-sm-12">
//                     <div className="card">
//                         <div className="card-body">
//                             <div className="table-responsive">
//                                 <table className="datatable table table-hover table-center mb-0">
//                                     <thead>
//                                         <tr>
//                                             <th>Payout ID</th>
//                                             <th>Date</th>
//                                             <th>Patient</th>
//                                             <th>Invoice ID</th>
//                                             <th>Amount</th>
//                                             <th>Status</th>
//                                             <th className="text-right">Actions</th>
//                                         </tr>
//                                     </thead>
//                                     <tbody>
//                                         {loading && !isSyncing ? (
//                                             <tr>
//                                                 <td colSpan="7" className="text-center py-4">
//                                                     <div className="spinner-border text-primary" role="status">
//                                                         <span className="sr-only">Loading...</span>
//                                                     </div>
//                                                     <p className="mt-2">Loading Payouts...</p>
//                                                 </td>
//                                             </tr>
//                                         ) : error ? (
//                                             <tr>
//                                                 <td colSpan="7" className="text-center text-danger py-4">
//                                                     Error: {error}
//                                                 </td>
//                                             </tr>
//                                         ) : currentItems.length > 0 ? (
//                                             currentItems.map((record) => (
//                                                 <tr key={record.id || record.payoutId}>
//                                                     <td>{record.payoutId || record.payoutid || 'N/A'}</td>
//                                                     <td>{formatDate(record.displayDate)}</td>
//                                                     <td>
//                                                         <h2 className="table-avatar">
//                                                             <Link to={`/patient-profile/${record.patientDetails.PatientID}`} className="avatar avatar-sm mr-2">
//                                                                 <img 
//                                                                     className="avatar-img rounded-circle" 
//                                                                     src={record.patientDetails.uploadPhoto ? `${imgUrl}${record.patientDetails.uploadPhoto}` : '/assets/img/patients/patient.jpg'} 
//                                                                     alt="Patient" 
//                                                                 />
//                                                             </Link>
//                                                             <Link to={`/patient-profile/${record.patientDetails.PatientID}`}>
//                                                                 {record.patientDetails.firstName || 'Unknown'} {record.patientDetails.lastName || ''}
//                                                             </Link>
//                                                         </h2>
//                                                     </td>
//                                                     <td>#{record.invoiceId || record.InvoiceId}</td>
//                                                     <td>{formatCurrency(record.Amount || 0, selectedCurrency)}</td>
//                                                     <td>
//                                                         <span className={`badge badge-pill ${getPayoutBadgeClass(record.displayStatus)}`}>
//                                                             {getDisplayStatus(record.displayStatus)}
//                                                         </span>
//                                                     </td>
//                                                     <td className="text-right">
//                                                         <button 
//                                                             className="btn btn-sm btn-info" 
//                                                             onClick={() => handleViewDetailsClick(record)}
//                                                         >
//                                                             View Details
//                                                         </button>
//                                                     </td>
//                                                 </tr>
//                                             ))
//                                         ) : (
//                                             <tr>
//                                                 <td colSpan="7" className="text-center py-4">
//                                                     No payout history found.
//                                                 </td>
//                                             </tr>
//                                         )}
//                                     </tbody>
//                                 </table>
//                             </div>

//                             {/* Pagination Controls */}
//                             {enrichedPayoutRecords.length > 0 && (
//                                 <div className="pagination-wrapper mt-3 d-flex justify-content-between align-items-center flex-wrap">
//                                     <div className="items-per-page-wrapper">
//                                         {renderItemsPerPageSelector()}
//                                     </div>
//                                     <div className="pagination-controls-wrapper">
//                                         <div className="dataTables_info mr-3">
//                                             Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, enrichedPayoutRecords.length)} of {enrichedPayoutRecords.length} entries
//                                         </div>
//                                         {renderPagination()}
//                                     </div>
//                                 </div>
//                             )}
//                         </div>
//                     </div>
//                 </div>
//             </div>

//             {/* Payout Details Modal */}
//             {selectedRecord && (
//                 <Modal show={showDetailsModal} onHide={handleCloseModal} centered>
//                     <Modal.Header className='bg-info text-white' closeButton closeVariant="white">
//                         <Modal.Title>Payout Record Details</Modal.Title>
//                     </Modal.Header>
//                     <Modal.Body>
//                         <p><strong>Database Record ID:</strong> {selectedRecord.id}</p>
//                         <p><strong>Payout ID (Gateway):</strong> <span className="text-primary">{selectedRecord.payoutId || selectedRecord.payoutid}</span></p>
//                         <hr/>
//                         <p><strong>Patient:</strong> {selectedRecord.patientDetails.firstName || 'Unknown'} {selectedRecord.patientDetails.lastName || ''}</p>
//                         <hr/>
//                         <p><strong>Invoice ID:</strong> #{selectedRecord.invoiceId || selectedRecord.InvoiceId}</p>
//                         <p><strong>Amount:</strong> {formatCurrency(selectedRecord.Amount || 0, selectedCurrency)}</p>
//                         <p><strong>Date:</strong> {formatDate(selectedRecord.displayDate)}</p>
//                         <p><strong>Status:</strong> {getDisplayStatus(selectedRecord.displayStatus)}</p>
//                     </Modal.Body>
//                     <Modal.Footer>
//                         <Button variant="secondary" onClick={handleCloseModal}>
//                             Close
//                         </Button>
//                     </Modal.Footer>
//                 </Modal>
//             )}
//         </Fragment>
//     );
// };

// export default PayoutHistory;